/**
 * When the « Tâches » page was last on screen, for the sidebar badge: a task
 * that failed after it turns the badge red (`tasksBadge`, tasks.ts).
 *
 * The time is the scaffolder's, not the browser's: the newest creation or
 * heartbeat of the tasks listed when the page was shown (`newestTaskTime`),
 * compared with the failed tasks' last heartbeat — one clock on both sides,
 * whatever the browser's clock says. Only an empty list falls back on the
 * browser's time (`recordVisit`).
 *
 * In `localStorage`, so that it survives a reload and is shared by the tabs
 * of the browser; one value per browser, not per user. A listener set at
 * module level tells the badge of a change made in the same tab, which the
 * `storage` event does not.
 */

import { useSyncExternalStore } from 'react';

export const LAST_VISIT_KEY = 'waadoo.tasks.lastVisit';

const listeners = new Set<() => void>();

/** `window.localStorage`, or undefined where reading it throws. */
function browserStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function readLastVisit(): number | undefined {
  const raw = browserStorage()?.getItem(LAST_VISIT_KEY);
  if (!raw) return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

export function writeLastVisit(at: number): void {
  try {
    browserStorage()?.setItem(LAST_VISIT_KEY, String(at));
  } catch {
    // Storage full or refused: the badge keeps the previous visit.
    return;
  }
  for (const listener of [...listeners]) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent) => {
    if (event.key === LAST_VISIT_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** The last visit, kept up to date. */
export function useLastVisit(): number | undefined {
  return useSyncExternalStore(subscribe, readLastVisit);
}

/**
 * Records a visit that saw `tasks`: their newest server time, else — none
 * listed — the browser's.
 */
export function recordVisit(newest: number | undefined): void {
  writeLastVisit(newest ?? Date.now());
}
